import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import type { AxiosResponse } from 'axios';
import type ApiLoyaltyCard from 'src/core/models/api/ApiLoyaltyCard';
import type ApiLoyaltyResponse from 'src/core/models/api/responses/ApiLoyaltyResponse';
import { reportOrRethrow } from 'src/core/utils/ApiCallbacks';
import toErrorMessage from 'src/core/utils/ErrorMessages';
import Translations from 'src/core/utils/Translations';
import ThermenRepository from 'src/data/repositories/ThermenRepository';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const initialState = {
	loyalty: null as ApiLoyaltyResponse | null,
	isLoading: false,
	errorMessage: null as string | null,
	selectedBenefit: null as ApiLoyaltyCard | null,
	resortPickerCard: null as ApiLoyaltyCard | null,
	entranceKey: 0,
};

export const useLoyaltyStore = create(
	combine({ ...initialState }, (set, get) => {
		const init = () =>
			set({
				isLoading: false,
				errorMessage: null,
				selectedBenefit: null,
				resortPickerCard: null,
			});

		// An open sheet holds a snapshot, so it would keep showing the state the card had before the refetch
		const refreshedCard = (data: ApiLoyaltyResponse, open: ApiLoyaltyCard | null) => {
			if (open === null) {
				return null;
			}

			return (data.benefits?.cards ?? []).find((card) => card.id === open.id) ?? null;
		};

		const getApiLoyalty = async ({
			onSuccess,
			onError,
			onFinally,
		}: {
			onSuccess?: (response: AxiosResponse<ApiLoyaltyResponse>) => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		} = {}) => {
			if (get().loyalty === null) {
				set({ isLoading: true });
			}
			set({ errorMessage: null });

			await ThermenRepository.getLoyalty()
				.then(async (response) => {
					const data: ApiLoyaltyResponse | null = response.data;

					if (!data) {
						set({ errorMessage: Translations.unknownErrorOccurred });
						return;
					}

					set({
						loyalty: data,
						selectedBenefit: refreshedCard(data, get().selectedBenefit),
						resortPickerCard: refreshedCard(data, get().resortPickerCard),
					});
					await onSuccess?.(response);
				})
				.catch((error: ApiRequestError) => {
					set({ errorMessage: toErrorMessage(error) });
					reportOrRethrow(error, onError);
				})
				.finally(() => {
					set({ isLoading: false });
					onFinally?.();
				});
		};

		const reset = () => set({ ...initialState });

		const openBenefit = (selectedBenefit: ApiLoyaltyCard) => set({ selectedBenefit });

		const closeBenefit = () => set({ selectedBenefit: null, resortPickerCard: null });

		// A card that a member can redeem at several resorts asks which one first, in the same sheet.
		const openResortPicker = (resortPickerCard: ApiLoyaltyCard) => set({ resortPickerCard });

		const closeResortPicker = () => set({ resortPickerCard: null });

		const replayEntrance = () => set({ entranceKey: get().entranceKey + 1 });

		return {
			init,
			getApiLoyalty,
			reset,
			openBenefit,
			closeBenefit,
			openResortPicker,
			closeResortPicker,
			replayEntrance,
		};
	}),
);
